import React from "react";
import { fileUrl } from "@/lib/api";
import { Store, ChevronRight } from "lucide-react";

/**
 * Renders a grid of sucursal thumbnails so the user picks one before seeing the list.
 * If exactly one sucursal is available, we auto-pick it (via the `autoPick` effect
 * inside the parent) — this component only renders when the user needs to choose.
 */
export default function SucursalPicker({ sucursales, onPick, title, subtitle }) {
  return (
    <div className="space-y-6" data-testid="sucursal-picker">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]">
          {title || "Elige una sucursal"}
        </h1>
        <p className="text-[#8A796F] mt-1">
          {subtitle || "Toca la sucursal en la que estás trabajando."}
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
        {sucursales.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s)}
            data-testid={`sucursal-pick-${s.name}`}
            className="group relative aspect-square rounded-3xl overflow-hidden border-2 border-[#EADBCE] bg-white shadow-sm hover:shadow-md hover:border-[#FF7878]/60 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F7C8E0] active:scale-[0.98] transition-all"
          >
            {s.image_path ? (
              <img
                src={fileUrl(s.image_path)}
                alt={s.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#F7C8E0] via-[#FFE7DA] to-[#A7E4D3] flex items-center justify-center">
                <Store className="w-12 h-12 text-white/80 drop-shadow" />
              </div>
            )}
            {/* Solid backdrop bar at the bottom guarantees legibility on any image */}
            <div className="absolute inset-x-0 bottom-0 bg-[#1F1710]/90 p-3 text-left">
              <div className="flex items-end justify-between gap-2">
                <span
                  className="font-display font-extrabold text-lg sm:text-xl leading-tight line-clamp-2"
                  style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}
                >
                  {s.name}
                </span>
                <ChevronRight
                  className="w-5 h-5 shrink-0 opacity-95 group-hover:translate-x-0.5 transition-transform"
                  style={{ color: "#FFFFFF", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }}
                />
              </div>
            </div>
          </button>
        ))}
      </div>

      {sucursales.length === 0 && (
        <div className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center">
          <Store className="mx-auto w-12 h-12 text-[#EADBCE]" />
          <h3 className="mt-4 font-display font-bold text-lg text-[#4A3B32]">
            Sin sucursales asignadas
          </h3>
          <p className="text-sm text-[#8A796F] mt-1">
            Pídele a tu Gerente que te asigne al menos una sucursal.
          </p>
        </div>
      )}
    </div>
  );
}
